import { FC, useMemo } from 'react';

import classNames from 'classnames';

import { formatDateLocaleDDMMYY } from '../../utils/dateUtils';
import { Check } from '../icons/check';
import { Cross } from '../icons/cross';

import { ITariffToolData, TariffTypes } from '../../store/tariff.slice';

import { Button, TextButton } from '../../shared/ui';

import { TariffCardLabel } from './tariff-card-label';
import { TariffCardHeader } from './tariff-card-header';
import { BonusInfoLabel } from './bonus-info-label';
import { ToolDescriptionIcon } from './tool-description-icon';
import { ITariffCardProps } from './tariff-card.types';

export const MinutesTariffCard: FC<ITariffCardProps> = ({
  tariff,
  displayMode,
  onDetailsClick,
  onSelectClick,
  tariffToolsMaxCount,
  active,
  scheduled,
  start_end_date,
  selectDisabled,
  hideRenew,
}) => {
  const isShotMode = displayMode === 'shot';
  const isPromoAction = tariff.type === TariffTypes.PROMO;
  const isFree = tariff.type === TariffTypes.FREE || tariff.price === 0;
  const toolItems: ITariffToolData[] = useMemo(() => {
    if (tariff.tools_data && tariff.tools_data.length) {
      return tariff.tools_data;
    }

    const fallbackNames = tariff.tool_names || [];
    const fallbackLabels = tariff.tool_labels || [];

    return fallbackNames.map((toolName, index) => ({
      name: fallbackLabels[index] ?? toolName,
      tool_name: toolName,
      tool_description: null,
    }));
  }, [tariff.tool_labels, tariff.tool_names, tariff.tools_data]);

  const discount = tariff.base_discount;
  const hasDiscount = discount > 0 && discount < 100;

  const tariffToolsFiller = useMemo(() => {
    const leftCount = tariffToolsMaxCount - toolItems.length;
    if (leftCount > 0) {
      return Array.from({ length: leftCount }).map((_, index) => {
        return <div key={`tool-filler-${index}`} className="min-h-[18px] h-[18]"></div>;
      });
    }

    return null;
  }, [tariffToolsMaxCount, toolItems.length]);

  return (
    <div
      className={classNames(
        `
        relative
       
        bg-white
        border-1
        rounded-card
        px-4
        pt-10
        flex
        flex-col
        w-[272px]
        min-w-[272px]
        md:h-full
      `,
        {
          'max-h-[456px]  h-[456px] overflow-hidden': isShotMode,
          'border-primary': isPromoAction,
          'border-accent': !isPromoAction,
        },
      )}
    >
      {tariff.is_popular && !isPromoAction && (
        <div className="absolute top-0 right-6  w-[100px] h-[45px]">
          <TariffCardLabel className={'bg-accent'} title="популярное" />
        </div>
      )}

      {isPromoAction && (
        <div className="absolute top-0 right-6  w-[100px] h-[45px]">
          <TariffCardLabel className={'bg-primary text-white'} title="акция" />
        </div>
      )}

      <TariffCardHeader tariff={tariff} />
      <p className="text-ui-xs text-center text-ink-subtle mb-2 min-h-[18px]">{tariff.description_2 || ' '}</p>

      <BonusInfoLabel
        limit={tariff.bonus_payment_limit_percent}
        classes={`${!tariff.is_bonus_payment_available ? 'invisible' : ''} text-ink-subtle`}
      />

      <div className={classNames('text-body-l-strong text-center mb-2', { invisible: !hasDiscount })}>
        <span className="text-ui-xs">скидка</span> <span className="text-danger">{discount}%</span>
      </div>

      <>
        {active ? (
          <div className="rounded-control px-4 text-center h-[34px] flex items-center bg-surface-muted">
            <p className="text-ink-subtle text-body-l-strong w-full text-center">
              Активно
              {start_end_date && ` До ${formatDateLocaleDDMMYY(new Date(start_end_date), 'ru-RU')}`}
            </p>
          </div>
        ) : (
          <Button
            size="sm"
            fullWidth
            variant={isFree ? 'neutral' : 'dark'}
            disabled={selectDisabled}
            onClick={() => onSelectClick?.()}
          >
            {isFree && !scheduled && 'Бесплатно'}
            {!isFree && !scheduled && 'Выбрать'}
            {scheduled && start_end_date && `Активно С ${formatDateLocaleDDMMYY(new Date(start_end_date), 'ru-RU')}`}
          </Button>
        )}
        <TextButton
          size="sm"
          tone="primary"
          className={classNames('mt-1', {
            invisible: !active || tariff.type !== TariffTypes.REGULAR || hideRenew,
          })}
          onClick={onSelectClick}
        >
          продлить тариф
        </TextButton>
        <ul className="flex-grow mt-1">
          {toolItems.slice(0, isShotMode ? 8 : undefined).map((tool, index) => {
            const isUnavailable = tool.limit !== null && tool.limit !== undefined && Number(tool.limit) === 0;

            return (
              <li key={`${tool.name ?? tool.tool_name}-${index}`} className="flex items-center">
                {isUnavailable ? <Cross fill="#9CA3AF" className="mr-1" /> : <Check fill="#C00000" className="mr-1" />}
                <div className="flex items-center min-w-0">
                  <span className="text-ui-xs whitespace-nowrap overflow-hidden text-ellipsis">
                    {tool.tool_name || tool.name}
                  </span>
                  {tool.tool_description && tool.tool_description.trim() !== '' && (
                    <ToolDescriptionIcon description={tool.tool_description} />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        {!isShotMode && tariffToolsFiller}
        {!isShotMode && (
          <p className="mt-2 text-ui-xs text-center text-ink-muted">
            Срок сгорания: {tariff.duration_days === 'unlimit' ? 'бессрочно' : `${tariff.duration_days} дней`}
          </p>
        )}
        {isShotMode && (
          <>
            <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-white to-transparent pointer-events-none z-[10]"></div>
            <div className="text-center z-[11] absolute bottom-0 left-0 right-0 bg-white h-[18px] flex justify-center items-end">
              <TextButton size="md" tone="primary" className="mb-2" onClick={() => onDetailsClick?.()}>
                подробнее
              </TextButton>
            </div>
          </>
        )}
        {!isShotMode && <div className="mb-5"></div>}
      </>
    </div>
  );
};
